import React from 'react';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';

export const DASHBOARD = {
    radius: '14px',
    radiusInner: '10px',
    padding: 2,
    rowMinHeight: 300,
    chartHeight: 220,
};

export const dashboardPaperSx = (theme) => ({
    p: DASHBOARD.padding,
    borderRadius: DASHBOARD.radius,
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: theme.palette.mode === 'dark'
        ? '0 1px 3px rgba(0,0,0,0.4)'
        : '0 1px 2px rgba(15,23,42,0.06)',
});

export const dashboardStretchPaperSx = (theme) => ({
    ...dashboardPaperSx(theme),
    flex: 1,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: DASHBOARD.rowMinHeight,
});

export const dashboardInnerCardSx = (theme) => ({
    p: 1.5,
    borderRadius: DASHBOARD.radiusInner,
    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
    border: '1px solid',
    borderColor: 'divider',
});

export const DashboardPanelHeader = ({ title, subtitle, icon, actions }) => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1.5,
            mb: 2,
            flexWrap: 'wrap',
        }}
    >
        <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {icon}
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                    {title}
                </Typography>
            </Box>
            {subtitle && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5, lineHeight: 1.35 }}
                >
                    {subtitle}
                </Typography>
            )}
        </Box>
        {actions}
    </Box>
);

export const DashboardSectionLabel = ({ children }) => (
    <Typography
        variant="caption"
        color="text.secondary"
        sx={{
            display: 'block',
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            mb: 0.75,
        }}
    >
        {children}
    </Typography>
);

export const DashboardPanelBody = ({ children, sx }) => (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, ...sx }}>
        {children}
    </Box>
);

export const DashboardEmptyState = ({ message, icon: Icon = InfoOutlined }) => (
    <Box
        sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            py: 4,
            textAlign: 'center',
            color: 'text.secondary',
        }}
    >
        <Icon sx={{ fontSize: 36, mb: 1, opacity: 0.45 }} />
        <Typography variant="body2">{message}</Typography>
    </Box>
);

export const DashboardLoadingState = () => (
    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress size={28} />
    </Box>
);

const DashboardPanel = ({ children, sx, theme, stretch = true }) => (
    <Paper sx={stretch ? { ...dashboardStretchPaperSx(theme), ...sx } : { ...dashboardPaperSx(theme), ...sx }}>
        {children}
    </Paper>
);

export default DashboardPanel;
